import React, { useRef, useState } from 'react';
import { 
  Download, Share2, Copy, CheckCircle, MapPin, Phone, Clock,
  Coffee, QrCode, Smartphone, MessageCircle, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

/**
 * TouristVoucher Component
 * 
 * A shareable/downloadable voucher that partners can give to tourists.
 * Contains partner code, 5% discount info, cafe location, and redemption instructions.
 */
export default function TouristVoucher({ partnerCode, partnerName }) {
  const voucherRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadVoucher = async () => {
    if (!voucherRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(voucherRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `Bulaccino-Voucher-${partnerCode}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Voucher downloaded! Share it with tourists.');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download voucher');
    }
    setDownloading(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(partnerCode);
    toast.success('Code copied to clipboard');
  };

  const shareVoucher = async () => {
    const shareText = `🌴 Get 5% OFF at Bulaccino Cafe!\n\nUse my partner code: ${partnerCode}\n\n📍 Martintar, Nadi (near Nadi Airport)\n⏰ Open 6:30am - 6pm daily\n\nJust mention the code when ordering!\n\n🌐 bulaccinocafe.com/order`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: '5% OFF at Bulaccino Cafe',
          text: shareText,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          navigator.clipboard.writeText(shareText);
          toast.success('Voucher text copied to clipboard');
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Voucher text copied to clipboard');
    }
  };

  const openMaps = () => {
    window.open('https://maps.google.com/?q=Bulaccino+Cafe+Nadi+Fiji', '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Voucher Card - This is what gets downloaded */}
      <div 
        ref={voucherRef}
        className="bg-white rounded-3xl overflow-hidden shadow-xl max-w-md mx-auto"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-600 text-white p-6 text-center">
          <div className="text-3xl font-bold mb-1">BULACCINO CAFE</div>
          <div className="text-sm opacity-90">Fiji's Finest Coffee Experience</div>
        </div>

        {/* Discount Banner */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 py-4 text-center">
          <div className="text-4xl font-black text-white drop-shadow-md">5% OFF</div>
          <div className="text-white/90 font-medium">YOUR ORDER</div>
        </div>

        {/* Partner Code */}
        <div className="p-6 text-center border-b border-dashed border-gray-300">
          <p className="text-sm text-gray-500 mb-2">Use this partner code:</p>
          <div className="bg-gray-100 rounded-xl p-4 inline-block">
            <span className="text-3xl font-mono font-bold text-green-700 tracking-wider">
              {partnerCode}
            </span>
          </div>
          {partnerName && (
            <p className="text-xs text-gray-400 mt-2">Courtesy of {partnerName}</p>
          )}
        </div>

        {/* How to Redeem */}
        <div className="p-6 bg-gray-50">
          <h3 className="font-bold text-gray-800 mb-3 text-center">How to Redeem</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">At Kiosk</p>
                <p className="text-gray-600">Enter code at checkout</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">At Counter</p>
                <p className="text-gray-600">Tell cashier: "I have partner code {partnerCode}"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Location & Hours */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-4 h-4 text-green-600" />
              <span>Martintar, Nadi</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-green-600" />
              <span>6:30am - 6pm</span>
            </div>
          </div>
          <div className="mt-3 text-center">
            <span className="text-xs text-gray-500">bulaccinocafe.com</span>
          </div>
        </div>

        {/* Footer Note */}
        <div className="bg-green-700 text-white text-center py-3 text-xs">
          Valid for all visits • No minimum purchase • Cannot combine with other offers
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <Button 
          onClick={downloadVoucher} 
          disabled={downloading}
          className="flex-1"
          data-testid="download-voucher-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? 'Downloading...' : 'Save to Phone'}
        </Button>
        <Button 
          onClick={shareVoucher}
          variant="outline"
          className="flex-1"
          data-testid="share-voucher-btn"
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share via WhatsApp/Text
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="flex justify-center gap-4 max-w-md mx-auto">
        <Button variant="ghost" size="sm" onClick={copyCode}>
          <Copy className="w-4 h-4 mr-1" />
          Copy Code
        </Button>
        <Button variant="ghost" size="sm" onClick={openMaps}>
          <MapPin className="w-4 h-4 mr-1" />
          Get Directions
        </Button>
      </div>

      {/* Instructions for Partner */}
      <div className="max-w-md mx-auto bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
        <h4 className="font-medium text-foreground mb-2">Tips for sharing:</h4>
        <ul className="space-y-1 list-disc list-inside">
          <li>Save the voucher image to your phone gallery</li>
          <li>Send via WhatsApp, Messenger, or text message</li>
          <li>Show the image to tourists directly</li>
          <li>Your code works for unlimited uses!</li>
        </ul>
      </div>
    </div>
  );
}
